import type { PagePermission } from "@superagents/types/api";
import { matchPath } from "react-router-dom";
import { RoutePaths } from "@/core/constants/RoutePaths";
import type { PlanFeatureKey } from "@/core/hooks/planFeature";

/**
 * Páginas que podem aparecer bloqueadas. Conversas nunca é bloqueada (D9).
 * Áreas (SA-1367) é uma aba dentro de Equipe, com gate próprio: não tem item
 * no menu, mas bloqueia e dá nome ao "Voltar para ..." como as outras.
 */
export type LockablePagePermission = Exclude<PagePermission, "ui:chats:view">;

type PageCopy = {
  /** Nome da página como no menu (Navbar). */
  label: string;
  /**
   * Rota de topo da página (a do item do menu). As sub-rotas herdam o nome:
   * `/settings/tags` é "Configurações" no "Voltar para ..." do bloqueio.
   */
  route: string;
  /** Frase do bloco "O que essa funcionalidade faz". */
  pitch: string;
  /**
   * Recurso do plano que a página exige além da chave (SA-1401). Fonte única:
   * o PermissionGate da rota, o menu e a origem do "Voltar para ..." leem
   * daqui. O backend também trava as ações fora do plano (403
   * `plan_feature_unavailable`, SA-1360 P4-A).
   */
  planFeature?: PlanFeatureKey;
  /**
   * Onde a trava de plano vale (SA-1360 P4-C). `"page"` (padrão): a página
   * inteira mostra o bloqueio de plano e o item do menu trava. `"actions"`: a
   * página abre fora do plano (o convite de upgrade fica dentro dela) e só as
   * rotas de `planActionRoutes` mostram o bloqueio cheio; no menu, item normal.
   */
  planScope?: "page" | "actions";
  /** Padrões de rota (React Router) que são ação, com `planScope: "actions"`. */
  planActionRoutes?: readonly string[];
};

/** O que decide a trava de plano de uma página (pageCopy ou seção do menu). */
export type PagePlanLock = Pick<
  PageCopy,
  "planFeature" | "planScope" | "planActionRoutes"
>;

/**
 * A trava de plano da página vale neste pathname? Página sem recurso de plano:
 * nunca. `planScope: "page"`: em toda sub-rota. `"actions"`: só nas rotas de
 * ação (ex.: instalar integração).
 */
export function pagePlanLocksPath(
  { planFeature, planScope = "page", planActionRoutes = [] }: PagePlanLock,
  pathname: string
): boolean {
  if (!planFeature) return false;
  if (planScope === "page") return true;
  return planActionRoutes.some(
    (pattern) => matchPath(pattern, pathname) !== null
  );
}

// Frases de produção do readme do DS (docs/design-system/readme.md › Bloqueio
// de recursos), verbatim.
export const LOCKABLE_PAGE_COPY: Record<LockablePagePermission, PageCopy> = {
  "ui:contacts:view": {
    label: "Contatos",
    route: RoutePaths.CONTACTS,
    pitch:
      "Sua base de clientes e leads em um só lugar, com histórico e listas.",
  },
  "ui:agent:view": {
    label: "Agente de IA",
    route: RoutePaths.AGENT,
    pitch:
      "Central de configuração do seu agente: edite o prompt, adicione arquivos de referência e defina os arquivos que ele pode enviar nas conversas.",
  },
  "ui:integrations:view": {
    label: "Integrações",
    route: RoutePaths.INTEGRATIONS,
    pitch:
      "Conecta seu agente ao CRM, ERP e outros sistemas da empresa para consultar dados em tempo real e atualizá-los automaticamente.",
    // O Mini não tem Integrações (SA-1360 P3; censo de 25/09: nenhum Mini com
    // integração do catálogo instalada, então a trava não tira nada de ninguém).
    planFeature: "hasIntegrations",
    // Decisão do time (25/09): o Mini abre o catálogo e o detalhe, sem
    // instalar; o bloqueio de plano vira convite dentro da página. Instalar e
    // reconfigurar (a rota de install) seguem com o bloqueio cheio.
    planScope: "actions",
    planActionRoutes: [RoutePaths.INTEGRATIONS_INSTALL],
  },
  "ui:follow-ups:view": {
    label: "Follow-ups",
    route: RoutePaths.FOLLOW_UPS,
    pitch: "Retoma sozinho quem não respondeu, no tempo que você definir.",
    planFeature: "hasFollowUps",
  },
  "ui:campaigns:view": {
    label: "Disparos",
    route: RoutePaths.CAMPAIGNS,
    pitch:
      "Envia uma mensagem para várias listas de contatos de uma vez, na hora ou de forma recorrente e automática.",
    planFeature: "hasBulkSend",
  },
  "ui:team-management:view": {
    label: "Equipe",
    route: RoutePaths.TEAM_MANAGEMENT,
    pitch: "Convida pessoas, organiza áreas e define o que cada um pode fazer.",
  },
  // Aba de Equipe (ruling R11): a rota fica dentro do gate de Equipe e a
  // frase vem do brief da SA-1367 (o readme do DS não tem uma para Áreas).
  "ui:areas:view": {
    label: "Áreas",
    route: RoutePaths.TEAM_MANAGEMENT_AREAS,
    pitch:
      "Divide a equipe em áreas e diz para a IA quando transferir cada conversa.",
  },
  "ui:dashboard:view": {
    label: "Dashboard",
    route: RoutePaths.DASHBOARD,
    pitch:
      "Mostra volume, tempo de resposta e desempenho por área e atendente.",
  },
  "ui:settings:view": {
    label: "Configurações",
    route: RoutePaths.SETTINGS,
    pitch:
      "Define como as conversas são distribuídas, encerradas e notificadas.",
  },
};

/**
 * Recursos de plano que travam o item do menu: os das páginas cuja trava vale
 * na rota de topo (a do item). Integrações abre fora do plano e fica de fora.
 */
export const MENU_PLAN_LOCKED_FEATURES: readonly PlanFeatureKey[] =
  Object.values(LOCKABLE_PAGE_COPY).flatMap((page) =>
    page.planFeature && pagePlanLocksPath(page, page.route)
      ? [page.planFeature]
      : []
  );
